import { Button, CalendarPopover, DateValue } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Box } from "styled-system/jsx";
import { DateRange } from "widgets/events/types";
import { useDateRangeCalendarPopOver } from "./useDateRangeCalendarPopOver";

type DateRangeSelectorProps = {
  dateRange: DateRange;
  calendarMaxDate: string;
  calendarMinDate: string;
  calendarSkippedDates: string[];
  onSelectedDatesChange: (dateRange: DateRange) => void;
};

export const DateRangeSelector = ({
  dateRange,
  calendarMaxDate,
  calendarMinDate,
  calendarSkippedDates,
  onSelectedDatesChange,
}: DateRangeSelectorProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET.BUTTONS" });

  const {
    focusedValue,
    isOpen,
    selectedDates,
    handleClick,
    handleSave,
    setIsOpen,
    setSelectedDates,
  } = useDateRangeCalendarPopOver({
    dateRange,
    startDate: dateRange.from,
    onSelectedDatesChange,
    onClose: () => setIsOpen(false),
  });

  return (
    <Box position="relative">
      {isOpen && (
        <Box
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          top="0"
          zIndex="1000"
          onClick={() => setIsOpen(false)}
        />
      )}
      {/* Note: tabIndex={0} is required to allow the calendar to be focused and to capture events in Safari*/}
      <CalendarPopover
        mode="range"
        tabIndex={0}
        value={selectedDates}
        focusedValue={focusedValue}
        onValueChange={({ valueAsString }: { valueAsString: string[] }) =>
          setSelectedDates(valueAsString)
        }
        Trigger={
          <Button
            part="date__btn-select-dates"
            visual="primary"
            mode="light"
            mr="1px"
            onClick={handleClick}
          >
            {dateRange.isFullMonth ? t("SELECT_DATES") : t("CLEAR_DATES")}...
          </Button>
        }
        BottomRightComponent={
          <Button
            visual="primary"
            mode="light"
            disabled={selectedDates.length < 2}
            onClick={handleSave}
          >
            {t("SAVE")}
          </Button>
        }
        onClearDatesClick={() => setSelectedDates([])}
        clearDatesLabel={t("CLEAR_DATES")}
        ctaClearDatesProps={{
          color: {
            base: "text.light",
            _hover: {
              base: "text.mid",
              _disabled: "text.light",
            },
            _disabled: "text.light",
          },
        }}
        disablePastDates={false}
        max={calendarMaxDate}
        min={calendarMinDate}
        isDateUnavailable={(date: DateValue) => {
          const dateString = `${date.year}-${`0${date.month}`.slice(-2)}-${`0${date.day}`.slice(-2)}`;
          return calendarSkippedDates.includes(dateString);
        }}
        open={isOpen}
        zIndex={"modal"}
        noPortal
      />

      {/*
      Note for the calendar settings above
      - `disablePastDates={false}` along with `minValue={format(startOfMonth(new Date()), EVENT_DATE_FORMAT)}`
        allows you to return to the current month when the month view is visible
      - `min={format(new Date(), EVENT_DATE_FORMAT)}` sets the minimum date that can be selected
      - If you open the modal and start clicking quickly, it will sometimes not let you return to the current month
        in the month view. I haven't experienced this if I wait a second before spamming the next month button
      */}
    </Box>
  );
};
